import type { Personnel } from './types';

export type PersonnelProfileDoc = Partial<Personnel> & Pick<Personnel, 'id'>;

export const PUBLIC_PERSONNEL_FIELDS = [
    'id',
    'name',
    'givenName',
    'familyName',
    'sortName',
    'preferredName',
    'pronouns',
    'category',
    'primaryRole',
    'title',
    'department',
    'isDepartmentHead',
    'productionDuties',
    'email',
    'phone',
    'deleted',
] as const satisfies readonly (keyof Personnel)[];

export const SENSITIVE_PERSONNEL_FIELDS = [
    'id',
    'emergencyName',
    'emergency',
    'emergencyPhone',
    'notes',
    'conflicts',
    'tracking',
    'linkedUserId',
    'rosterPersonId',
    'inviteQueue',
    'bio',
    'headshotUrl',
    'headshotFileId',
    'headshotStoragePath',
    'accessNeeds',
    'allergies',
    'measurementNotes',
    'notificationPreference',
    'emailOptOut',
] as const satisfies readonly (keyof Personnel)[];

function pickDefinedPersonnelFields(
    personnel: Personnel,
    fields: readonly (keyof Personnel)[]
): PersonnelProfileDoc {
    const profile: PersonnelProfileDoc = { id: personnel.id };
    for (const field of fields) {
        const value = personnel[field];
        if (value !== undefined) {
            Object.assign(profile, { [field]: value });
        }
    }
    return profile;
}

export function toPublicPersonnelProfile(personnel: Personnel): PersonnelProfileDoc {
    return pickDefinedPersonnelFields(personnel, PUBLIC_PERSONNEL_FIELDS);
}

export function toSensitivePersonnelProfile(personnel: Personnel): PersonnelProfileDoc {
    return pickDefinedPersonnelFields(personnel, SENSITIVE_PERSONNEL_FIELDS);
}

export function composePersonnelProfiles(
    publicProfiles: PersonnelProfileDoc[],
    sensitiveProfiles: PersonnelProfileDoc[] = []
): Personnel[] {
    const sensitiveById = new Map(sensitiveProfiles.map(profile => [profile.id, profile]));
    return publicProfiles.map(publicProfile => ({
        ...publicProfile,
        ...sensitiveById.get(publicProfile.id),
    } as Personnel));
}
